/**
 * SOA Y HRA — los dos papeles que acompañan a una venta de Medicare.
 *
 * Lissette, 2026-09-13: *"quiero que en las fichas de medicare coloques un
 * campo que diga estatus del SOA y que sea un drop down que lo vayan llenando
 * y vaya quedando, y al lado SOA firmado y de la opción firmado o no firmado,
 * y también agrega una casilla que se llame HRA y sea un drop down igual"*.
 *
 * QUÉ SON, para quien lea esto sin saberlo. El **SOA** (Scope of Appointment)
 * es el permiso que el cliente firma ANTES de que el agente le hable de un
 * plan: CMS lo exige y sin él la cita no debería ocurrir. El **HRA** (Health
 * Risk Assessment) es el cuestionario de salud que muchas aseguradoras piden
 * después de inscribir. Los dos son trámites con estado —pendiente, enviado,
 * recibido— y los dos se pierden si nadie los va marcando: por eso la agente
 * los quiere en la ficha y no en un Excel aparte.
 *
 * ── POR QUÉ UN DESPLEGABLE QUE ADEMÁS DEJA ESCRIBIR ────────────────────────
 *
 * Es la misma decisión, y el mismo código, que el TIPO DE SUPLEMENTO
 * (`tiposDeSuplemento.js`, 2026-09-08): una lista cerrada obligaría a que
 * estuviera completa el primer día, y no lo está — cada aseguradora y cada
 * agencia nombra sus trámites a su manera. Y lo que llega de un IMPORTE es el
 * texto que escribió otra persona: si no está en la lista, la opción no es
 * descartarlo, es guardarlo tal cual.
 *
 *   1. Las listas de abajo son SUGERENCIAS, no un enum.
 *   2. Se les suman los valores que la agencia YA usó, así que el catálogo
 *      crece solo y sin que nadie lo configure. Eso es el *"que lo vayan
 *      llenando y vaya quedando"* que pidió, sin una pantalla de ajustes que
 *      alguien tenga que mantener.
 *   3. Cualquier texto se acepta y se guarda como llegó.
 *
 * LA EXCEPCIÓN ES `SOA firmado`, y es a propósito: ahí ella SÍ dictó las dos
 * opciones ("firmado o no firmado"). Un sí/no con la puerta abierta a texto
 * libre acabaría con "Firmado", "FIRMADO", "si", "Signed" y "OK" repartidos
 * por la cartera, y entonces no se puede contar cuántos faltan — que es la
 * única pregunta que se le hace a ese campo.
 *
 * NO HACE FALTA MIGRACIÓN: viajan en `type_details` (POLICY_DETAIL_KEYS de
 * api.js), el mismo sitio donde ya viven el MBI, las fechas de Parte A/B y el
 * tipo de suplemento.
 */

/** Estatus del SOA que se ofrecen de fábrica. Sugerencias, no lista cerrada. */
export const ESTATUS_DE_SOA = [
  'Pendiente',
  'Enviado al cliente',
  'Firmado y recibido',
  'Vencido',
  'No aplica',
];

/** Estatus del HRA. Mismo criterio. */
export const ESTATUS_DE_HRA = [
  'Pendiente',
  'Enviado al cliente',
  'Completado',
  'Enviado a la compañía',
  'No aplica',
];

/**
 * ELEGIBILIDAD DE MEDICAID — las categorías duales de CMS.
 *
 * Lissette, 2026-09-14: *"quiero que en las tarjetas del Medicare todas lleven
 * Medicaid elegibility, con estas opciones y todo en mayúscula: FULL MEDICAID,
 * QMB, QMB PLUS, SLMB, SLBM PLUS, QI, FBDE — y poder agregar más si quieres"*.
 *
 * Qué son, para quien lea esto sin saberlo: un cliente de Medicare puede tener
 * ADEMÁS Medicaid, y de qué manera lo tenga decide quién paga su prima, sus
 * deducibles y sus copagos. **QMB** paga prima y copagos; **SLMB** solo la
 * prima de la Parte B; **QI** también, pero con fondos limitados; **FBDE** es
 * el beneficio completo; **QDWI** es el caso del incapacitado que trabaja.
 * Saber cuál tiene cada cliente es lo que evita inscribirlo en un plan que le
 * va a cobrar algo que no puede pagar.
 *
 * DOS COSAS QUE ELLA NO DIJO Y VAN:
 *   - **QDWI**, la sexta categoría de CMS. Si no está, quien la tenga la
 *     escribiría a mano de cinco formas distintas.
 *   - **NO MEDICAID**, que NO es lo mismo que dejarlo vacío: vacío es *"no lo
 *     hemos preguntado"* y esto es *"lo preguntamos y no tiene"*. Sin esa
 *     diferencia, nadie sabe a quién le falta preguntarle.
 *
 * Y UNA QUE SÍ DIJO, CORREGIDA: escribió *"SLBM PLUS"*. Ese código no existe —
 * es **SLMB** (Specified Low-Income Medicare Beneficiary), como ella misma lo
 * escribió dos palabras antes. Se guarda bien escrito, y el normalizador de
 * abajo recoge la errata (suya o de quien mande la hoja) y la endereza, porque
 * si no el catálogo se parte en dos y la cuenta de "cuántos QMB tengo" miente.
 */
export const ELEGIBILIDAD_DE_MEDICAID = [
  'FULL MEDICAID',
  'QMB',
  'QMB PLUS',
  'SLMB',
  'SLMB PLUS',
  'QI',
  'QDWI',
  'FBDE',
  'NO MEDICAID',
];

/**
 * NIVEL DE LIS (Low Income Subsidy, la "Extra Help" de la Parte D). El
 * desplegable se va llenando con lo que la agencia use, como el del SOA.
 *
 * Lissette 2026-10-15: *"cambia esos valores por LIS LEVEL 3, LIS LEVEL 2,
 * LIS LEVEL 1, NO LIS y quita eso que colocaste"*. Los porcentajes de antes
 * (`NIVELES_DE_LIS_RETIRADOS`) ya no se ofrecen; una póliza que ya tenga uno
 * guardado lo sigue enseñando (no se borra nada) hasta que se cambie.
 */
export const NIVELES_DE_LIS = ['LIS LEVEL 3', 'LIS LEVEL 2', 'LIS LEVEL 1', 'NO LIS'];
export const NIVELES_DE_LIS_RETIRADOS = ['LIS 100%', 'LIS 75%', 'LIS 50%', 'LIS 25%'];

/** Lo usado en otras pólizas, sin los porcentajes retirados. */
export function lisUsadosVigentes(usados) {
  return (usados || []).filter((v) => !NIVELES_DE_LIS_RETIRADOS.includes(String(v || '').trim()));
}

/** Como se compara: MAYÚSCULAS, el "+" escrito PLUS y sin puntos ni guiones. */
function llaveDeCodigo(v) {
  return String(v == null ? '' : v)
    .toUpperCase()
    .replace(/\+/g, ' PLUS ')
    .replace(/[^A-Z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Lo que otra persona escribe para decir lo mismo -> el código bueno. */
const CODIGOS_DE_MEDICAID = new Map([
  ['FULL MEDICAID', 'FULL MEDICAID'], ['MEDICAID COMPLETO', 'FULL MEDICAID'],
  ['FULL', 'FULL MEDICAID'], ['MEDICAID FULL', 'FULL MEDICAID'],
  ['QMB', 'QMB'], ['QMB ONLY', 'QMB'], ['QMB PLUS', 'QMB PLUS'],
  // SLBM no existe: es la errata de SLMB, y viene en la petición original.
  ['SLMB', 'SLMB'], ['SLBM', 'SLMB'], ['SLMB ONLY', 'SLMB'],
  ['SLMB PLUS', 'SLMB PLUS'], ['SLBM PLUS', 'SLMB PLUS'],
  ['QI', 'QI'], ['QI 1', 'QI'], ['QI1', 'QI'],
  ['QDWI', 'QDWI'],
  ['FBDE', 'FBDE'],
  ['NO MEDICAID', 'NO MEDICAID'], ['SIN MEDICAID', 'NO MEDICAID'],
  ['NONE', 'NO MEDICAID'], ['NINGUNO', 'NO MEDICAID'], ['NO', 'NO MEDICAID'],
]);

/**
 * "qmb+", "SLBM plus", "medicaid completo" -> el código en mayúscula.
 *
 * Lo que NO reconoce se guarda **tal cual se escribió**: puede ser una nota de
 * la agente ("pendiente de verificar con el estado") y gritársela en mayúsculas
 * no la mejora. Misma decisión que `normalizarFirmaDeSoa`.
 */
export function normalizarElegibilidadDeMedicaid(v) {
  const bruto = String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
  if (!bruto) return '';
  return CODIGOS_DE_MEDICAID.get(llaveDeCodigo(bruto)) || bruto;
}

/**
 * ¿El SOA está firmado? Las DOS opciones que ella dictó, y ninguna más.
 *
 * Se guarda la palabra en español porque es lo que se ve y lo que se exporta;
 * `normalizarFirmaDeSoa` recoge lo que venga de un importe en inglés o mal
 * escrito y lo trae a una de estas dos.
 */
export const FIRMA_DEL_SOA = ['Firmado', 'No firmado'];

// Lo que otra persona escribe en su hoja para decir lo mismo. Solo variantes
// que no dejan dudas: ante algo raro se guarda tal cual y que lo lea un humano.
const FIRMAS_EQUIVALENTES = [
  [/^(no\s*firmado|no?t?\s*signed|unsigned|sin\s*firma(r)?|pendiente\s*de\s*firma)$/i, 'No firmado'],
  [/^(firmado|signed|si|s[ií]|yes|y|ok|completo|completado)$/i, 'Firmado'],
];

/** "signed", "SI", "sin firmar" -> "Firmado" / "No firmado". Lo raro no se toca. */
export function normalizarFirmaDeSoa(v) {
  const s = String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
  if (!s) return '';
  // El NO primero: "no firmado" contiene "firmado" y el orden decide.
  for (const [re, valor] of FIRMAS_EQUIVALENTES) {
    if (re.test(s)) return valor;
  }
  return s;
}

/**
 * Lo que se ofrece en un desplegable: las de fábrica, MÁS las que la agencia ya
 * usó, MÁS la que tenga puesta esta póliza — sin repetidos y sin perder la
 * actual, que es la que se borraría sola al abrir el formulario.
 */
export function opcionesConLasUsadas(deFabrica, usados = [], actual = '') {
  const limpios = (Array.isArray(usados) ? usados : [])
    .map((x) => String(x == null ? '' : x).trim())
    .filter(Boolean);
  const todos = [...(Array.isArray(deFabrica) ? deFabrica : []), ...limpios];
  const ahora = String(actual || '').trim();
  if (ahora) todos.push(ahora);
  const vistos = new Set();
  const salida = [];
  for (const t of todos) {
    const llave = t.toLowerCase();
    if (vistos.has(llave)) continue;
    vistos.add(llave);
    salida.push(t);
  }
  return salida;
}

/** Los valores de una clave de `type_details` que la agencia ya tiene puestos. */
export function valoresUsados(polizas = [], clave = '') {
  const out = [];
  for (const p of (Array.isArray(polizas) ? polizas : [])) {
    const v = p && p[clave];
    const s = String(v == null ? '' : v).trim();
    if (s) out.push(s);
  }
  return out;
}
